import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './FilterChipDropdown.module.scss';
import { StatusOptions } from 'Components/Status/StatusOption';
import { CONTENT_TYPE_OPTIONS, CONTENT_TYPE_ICONS } from 'constants/contentTypes';
import { useSelector } from 'react-redux';
import useProjectUsers from 'hooks/projects/useProjectUsers';
import useProjects from 'hooks/projects/useProjects';

/**
 * Simple, standalone dropdown for editing filter chips
 * Renders via Portal to avoid ANY overflow issues
 */
const FilterChipDropdown = ({
  filterBy,
  filterValue,
  onSelect,
  onClose,
  position, // { top, left }
  project,
  triggerRef, // Reference to the chip that triggered this dropdown
}) => {
  const dropdownRef = useRef(null);
  const { user } = useSelector((state) => state.auth);
  const { user_brands } = user || {};
  const { data: projectUsers } = useProjectUsers({ project });
  const { data: projectsData } = useProjects({ searchTerm: '', enabled: true });

  // Close on outside click (but not when clicking the trigger chip)
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Don't close if clicking inside dropdown
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) {
        return;
      }

      // Don't close if clicking the trigger chip (let chip's onClick handle it)
      if (triggerRef?.current && triggerRef.current.contains(e.target)) {
        return;
      }

      // Otherwise, close the dropdown
      onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, triggerRef]);

  // Get options based on filter type
  const getOptions = () => {
    switch (filterBy) {
      case 'state':
        return StatusOptions.map(({ value }) => ({
          value,
          label: value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        }));

      case 'content_type':
        return CONTENT_TYPE_OPTIONS.map(({ label, value }) => ({
          value,
          label,
          icon: CONTENT_TYPE_ICONS[value]
        }));

      case 'assignee_id':
        const users = project
          ? (projectUsers?.data?.project_users || []).map(({ user }) => user)
          : [user];
        return users.map(u => ({ value: u.id, label: u.name }));

      case 'brand_id':
        const brandsData = user_brands?.brands_data || [];
        return brandsData.map(brandObj => {
          const brandId = Object.keys(brandObj)[0];
          const brandData = Object.values(brandObj)[0];
          const brandName = brandData?.brand_name || brandData || 'Unknown';
          return { value: Number(brandId), label: brandName };
        });

      case 'project_id':
        const allProjects = projectsData?.pages?.flatMap(
          (page) => page?.data?.projects || []
        ) || [];
        return allProjects.map(proj => ({ value: proj.id, label: proj.name }));

      default:
        return [];
    }
  };

  const options = getOptions();

  console.log('🚀 FilterChipDropdown rendering!', {
    filterBy,
    optionsCount: options.length,
    options: options.map(o => o.label)
  });

  const isSelected = (value) => {
    return Array.isArray(filterValue) &&
           filterValue.some(v => v.value === value);
  };

  const handleToggle = (option) => {
    onSelect(option);
  };

  // Render via Portal
  return createPortal(
    <div
      ref={dropdownRef}
      className={styles.dropdown}
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 999999,
      }}
    >
      <div className={styles.optionsList}>
        {options.map((option) => (
          <div
            key={option.value}
            className={`${styles.option} ${isSelected(option.value) ? styles.optionSelected : ''}`}
            onClick={() => handleToggle(option)}
          >
            <input
              type="checkbox"
              checked={isSelected(option.value)}
              onChange={() => {}}
              className={styles.checkbox}
            />
            {option.icon && (
              <span className={styles.icon}>{option.icon}</span>
            )}
            <span className={styles.label}>{option.label}</span>
          </div>
        ))}
      </div>
    </div>,
    document.body
  );
};

export default FilterChipDropdown;
